import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { 
  LayoutDashboard, 
  Landmark,
  Wallet, 
  CreditCard, 
  Receipt, 
  TrendingDown, 
  Settings,
  LogOut, 
  Menu, 
  X, 
  ChevronLeft, 
  ChevronRight 
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

// Navigation items
const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/envelopes', label: 'Envelopes', icon: Wallet },
  { path: '/transactions', label: 'Transactions', icon: Receipt },
  { path: '/accounts', label: 'Accounts', icon: Landmark },
  { path: '/income', label: 'Income', icon: CreditCard },
  { path: '/debts', label: 'Debt Planner', icon: TrendingDown },
  { path: '/settings', label: 'Settings', icon: Settings }
];

export default function Layout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background text-text-body font-sans">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-20 bg-background-card border-b border-border px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-md text-text-muted hover:text-text-header hover:bg-background-subtle transition-colors"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
        <h1 className="text-lg font-bold text-text-header">Budget</h1>
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Mobile sidebar overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-screen transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          ${isSidebarOpen ? 'w-64' : 'w-20'}
          bg-background-card border-r border-border
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {isSidebarOpen && (
            <h1 className="text-xl font-bold text-text-header bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">
              Budget
            </h1>
          )}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="hidden lg:flex p-1.5 rounded-md text-text-muted hover:text-text-header hover:bg-background-subtle transition-colors"
          >
            {isSidebarOpen ? (
              <ChevronLeft className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center px-3 py-2.5 rounded-lg transition-all duration-200 group relative ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-text-muted hover:text-text-header hover:bg-background-subtle'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && isSidebarOpen && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-1 bg-primary rounded-r-full" />
                  )}

                  <item.icon
                    className={`flex-shrink-0 transition-colors ${isSidebarOpen ? 'h-5 w-5' : 'h-6 w-6'}`}
                    strokeWidth={2}
                  />

                  {isSidebarOpen ? (
                    <span className="ml-3 font-medium">{item.label}</span>
                  ) : (
                    <div className="absolute left-full ml-4 px-2 py-1 bg-background-card border border-border rounded text-xs text-text-header whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl">
                      {item.label}
                    </div>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-background-card">
          <button
            onClick={handleLogout}
            className={`
              flex items-center w-full px-3 py-2.5 rounded-lg
              text-text-muted hover:text-danger hover:bg-danger-bg transition-all duration-200 group
            `}
          >
            <LogOut className={`flex-shrink-0 transition-colors ${isSidebarOpen ? 'h-5 w-5' : 'h-6 w-6'}`} />
            {isSidebarOpen && (
              <span className="ml-3 font-medium">Logout</span>
            )}
            {!isSidebarOpen && (
               <div className="absolute left-full ml-4 px-2 py-1 bg-background-card border border-border rounded text-xs text-text-header whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl">
                  Logout
               </div>
            )}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={`
          transition-all duration-300 ease-in-out
          pt-16 lg:pt-0
          ${isSidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
        `}
      >
        <div className="min-h-screen p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
